/**
 * DocumentMetadataPanel — right-anchored fixed overlay showing metadata for a
 * selected Knowledge Object.
 *
 * Shown by clicking the info icon on a document row. Displays all stable
 * metadata fields: original filename, MIME type, file size, page count,
 * ingestion date, conversion date, char count, and knowledge layer counts.
 * Future fields (custom metadata, tags, provenance) can be added as extra rows
 * without touching the page layout.
 */
import { IconButton, Tag, InlineLoading } from '@carbon/react';
import { Close } from '@carbon/icons-react';
import type { KBDocument } from '@shared/types/knowledge-base';
import { deriveKOStatus } from '@shared/utils/knowledge-object-status';

export interface DocumentMetadataPanelProps {
  doc: KBDocument | null;
  onClose: () => void;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ─── MetaRow ──────────────────────────────────────────────────────────────────

function MetaRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.125rem',
        padding: '0.75rem 1rem',
        borderBottom: '1px solid var(--cds-border-subtle)',
      }}
    >
      <span
        style={{
          fontSize: '0.6875rem',
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--cds-text-secondary)',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: '0.875rem',
          color: 'var(--cds-text-primary)',
          wordBreak: 'break-all',
        }}
      >
        {value}
      </span>
    </div>
  );
}

// ─── SectionHeader ────────────────────────────────────────────────────────────

function SectionHeader({ title }: { title: string }) {
  return (
    <div
      style={{
        padding: '0.75rem 1rem 0.25rem',
        fontSize: '0.6875rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'var(--cds-text-secondary)',
        background: 'var(--cds-layer-02)',
        borderBottom: '1px solid var(--cds-border-subtle)',
      }}
    >
      {title}
    </div>
  );
}

// ─── Panel ────────────────────────────────────────────────────────────────────

export function DocumentMetadataPanel({ doc, onClose }: DocumentMetadataPanelProps) {
  if (!doc) return null;

  const l1 = doc.knowledge_unit_count ?? 0;
  const l2 = doc.summary_count ?? 0;
  const l3 = doc.extraction_count ?? 0;
  const statusInfo = deriveKOStatus(doc.conversion_status, l1, l2, l3, doc.layers_status);

  const displayName = doc.display_name || doc.filename;
  const isRenamed = !!doc.display_name && doc.display_name !== doc.filename;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.2)',
          zIndex: 8999,
        }}
      />

      {/* Panel */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          width: '360px',
          background: 'var(--cds-layer-01)',
          borderLeft: '1px solid var(--cds-border-subtle)',
          boxShadow: '-4px 0 16px rgba(0,0,0,0.08)',
          zIndex: 9000,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            padding: '1rem',
            borderBottom: '1px solid var(--cds-border-subtle)',
            gap: '0.5rem',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'var(--cds-text-primary)',
                wordBreak: 'break-word',
                lineHeight: 1.4,
              }}
            >
              {displayName}
            </div>
            <div style={{ marginTop: '0.375rem' }}>
              <Tag type={statusInfo.tagType} size="sm">
                {statusInfo.label}
              </Tag>
            </div>
          </div>
          <IconButton kind="ghost" size="sm" label="Close panel" onClick={onClose} style={{ flexShrink: 0 }}>
            <Close />
          </IconButton>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* File */}
          <SectionHeader title="File" />
          {isRenamed && (
            <MetaRow label="Original filename" value={doc.filename} />
          )}
          <MetaRow label="Display name" value={isRenamed ? doc.display_name! : <span style={{ color: 'var(--cds-text-secondary)' }}>—</span>} />
          <MetaRow label="MIME type" value={doc.mime_type || '—'} />
          <MetaRow label="File size" value={formatFileSize(doc.file_size)} />
          {doc.page_count != null && (
            <MetaRow label="Pages" value={doc.page_count} />
          )}
          {doc.text_char_count != null && (
            <MetaRow
              label="Characters (markdown)"
              value={doc.text_char_count.toLocaleString()}
            />
          )}

          {/* Ingestion */}
          <SectionHeader title="Ingestion" />
          <MetaRow label="Ingested" value={formatDate(doc.uploaded_at)} />
          <MetaRow label="Converted" value={formatDate(doc.converted_at)} />
          {doc.conversion_status === 'failed' && doc.error_message && (
            <MetaRow
              label="Error"
              value={
                <span style={{ color: 'var(--cds-support-error)', fontSize: '0.8125rem' }}>
                  {doc.error_message}
                </span>
              }
            />
          )}

          {/* Knowledge layers */}
          {(l1 > 0 || l2 > 0 || l3 > 0) && (
            <>
              <SectionHeader title="Knowledge layers" />
              {l1 > 0 && <MetaRow label="L1 — chunks" value={l1.toLocaleString()} />}
              {l2 > 0 && (
                <MetaRow
                  label="L2 — summaries"
                  value={`${l2.toLocaleString()}${doc.summary_eligible_count ? ` / ${doc.summary_eligible_count.toLocaleString()} eligible` : ''}`}
                />
              )}
              {l3 > 0 && (
                <MetaRow
                  label="L3 — extractions"
                  value={`${l3.toLocaleString()}${doc.extraction_eligible_count ? ` (${(doc.extraction_unit_count ?? 0).toLocaleString()} units / ${doc.extraction_eligible_count.toLocaleString()} eligible)` : ''}`}
                />
              )}
            </>
          )}

          {/* Processing (in-progress feedback) */}
          {doc.layers_status === 'processing' && (
            <div style={{ padding: '0.75rem 1rem' }}>
              <InlineLoading description="Processing knowledge layers…" />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
